// Coleção de cosméticos (lote 3, item 15): mesa (feltro e borda), verso das cartas e moldura do rosto, desenhados em
// SVG com os tokens do design system (o desenho de cada visual fica no jogo; aqui, o catálogo e as regras). O catálogo
// fica na tabela cosmeticos (seed por migração; este é o mesmo, para a memória e os testes).
// Origem de cada item: plano (de graça no plano), loja (preço em Tentos e, a partir de um plano, de graça),
// recompensa ou passe. Item de recompensa ou de passe é exclusivo e nunca vai para a loja.
// Grátis tem 1 de cada categoria, Bronze libera mais 3, Prata mais 6 e Ouro todos os itens de loja. A descida de plano
// tira só os itens do plano; o que foi comprado, recompensado ou ganho no passe fica.
import { PLANOS, type Plano } from '@truco6/planos';

export const CATEGORIAS_DE_COSMETICO = ['mesa', 'verso', 'moldura'] as const;
export type CategoriaCosmetico = (typeof CATEGORIAS_DE_COSMETICO)[number];
export const ORIGENS_DE_COSMETICO = ['plano', 'loja', 'recompensa', 'passe'] as const;
export type OrigemCosmetico = (typeof ORIGENS_DE_COSMETICO)[number];

export interface Cosmetico {
  id: string;
  categoria: CategoriaCosmetico;
  nome: string;
  origem: OrigemCosmetico;
  plano: Plano | null; // o plano a partir do qual o item vem de graça (origem plano ou loja)
  preco: number | null; // em Tentos, só na loja
  como: string; // de onde vem, para a tela Coleção
  ordem: number;
}

const c = (id: string, categoria: CategoriaCosmetico, nome: string, origem: OrigemCosmetico, plano: Plano | null, preco: number | null, como: string, ordem: number): Cosmetico =>
  ({ id, categoria, nome, origem, plano, preco, como, ordem });

export const CATALOGO_DE_COSMETICOS: readonly Cosmetico[] = [
  c('mesa_feltro_classico', 'mesa', 'Feltro Clássico', 'plano', 'gratis', null, 'Todos os planos', 1),
  c('mesa_boteco_azul', 'mesa', 'Boteco Azul', 'loja', 'bronze', 15, 'Loja ou plano Bronze', 2),
  c('mesa_couro_vinho', 'mesa', 'Couro Vinho', 'loja', 'prata', 25, 'Loja ou plano Prata', 3),
  c('mesa_de_ouro', 'mesa', 'Mesa de Ouro', 'loja', 'ouro', 40, 'Loja ou plano Ouro', 4),
  c('mesa_fogueira', 'mesa', 'Fogueira', 'passe', null, null, 'Passe da Temporada, trilha do assinante', 5),
  c('verso_casa', 'verso', 'Casa', 'plano', 'gratis', null, 'Todos os planos', 11),
  c('verso_azulejo', 'verso', 'Azulejo', 'loja', 'bronze', 15, 'Loja ou plano Bronze', 12),
  c('verso_dourado', 'verso', 'Dourado', 'loja', 'ouro', 30, 'Loja ou plano Ouro', 13),
  c('verso_grao', 'verso', 'Grão de Ouro', 'recompensa', null, null, 'Primeira compra de Tentos', 14),
  c('verso_vulcao', 'verso', 'Vulcão', 'recompensa', null, null, 'Ofensiva de 365 dias', 15),
  c('verso_fogueira', 'verso', 'Fogueira', 'passe', null, null, 'Passe da Temporada, trilha do assinante', 16),
  c('moldura_madeira', 'moldura', 'Madeira', 'plano', 'gratis', null, 'Todos os planos', 21),
  c('moldura_bronze', 'moldura', 'Bronze', 'loja', 'bronze', 10, 'Loja ou plano Bronze', 22),
  c('moldura_prata', 'moldura', 'Prata', 'loja', 'prata', 10, 'Loja ou plano Prata', 23),
  c('moldura_creme', 'moldura', 'Creme', 'loja', 'prata', 10, 'Loja ou plano Prata', 24),
  c('moldura_feltro', 'moldura', 'Feltro', 'loja', 'ouro', 10, 'Loja ou plano Ouro', 25),
  c('moldura_dourada', 'moldura', 'Dourada', 'loja', 'ouro', 20, 'Loja ou plano Ouro', 26),
  c('moldura_chama', 'moldura', 'Chama', 'recompensa', null, null, 'Ofensiva de 7 dias', 27),
  c('moldura_brasa', 'moldura', 'Brasa', 'passe', null, null, 'Passe da Temporada, trilha grátis', 28),
  c('moldura_fogueira', 'moldura', 'Fogueira', 'passe', null, null, 'Passe da Temporada, trilha do assinante', 29),
];

// O que cada categoria usa quando nada está equipado (ou o equipado saiu com a descida de plano).
export const PADRAO_DA_CATEGORIA: Record<CategoriaCosmetico, string> = { mesa: 'mesa_feltro_classico', verso: 'verso_casa', moldura: 'moldura_madeira' };

// Recompensas que dão cosmético (item 11).
export const COSMETICO_DA_RECOMPENSA = { ofensiva_7: 'moldura_chama', ofensiva_365: 'verso_vulcao', primeira_compra: 'verso_grao' } as const;

const ordemDoPlano = (p: Plano) => PLANOS.indexOf(p);

// Itens que o plano libera de graça: os do plano e os de loja a partir daquele plano; o Ouro, todos os de loja.
export function doPlano(catalogo: readonly Cosmetico[], plano: Plano): Cosmetico[] {
  return catalogo.filter((x) => (x.origem === 'plano' || x.origem === 'loja') && (
    (x.plano !== null && ordemDoPlano(x.plano) <= ordemDoPlano(plano)) || (plano === 'ouro' && x.origem === 'loja')));
}

// A loja: só itens de origem loja, com preço. Exclusivo de recompensa ou passe nunca entra.
export function daLoja(catalogo: readonly Cosmetico[]): Cosmetico[] {
  return catalogo.filter((x) => x.origem === 'loja' && x.preco !== null && x.preco > 0);
}

// O que o jogador pode equipar: o que o plano libera mais o que ficou na conta (comprado, recompensa, passe).
export function disponiveis(catalogo: readonly Cosmetico[], plano: Plano, naConta: Iterable<string>): Set<string> {
  const ids = new Set(doPlano(catalogo, plano).map((x) => x.id));
  for (const id of naConta) if (catalogo.some((x) => x.id === id)) ids.add(id);
  return ids;
}

export type Equipados = Record<CategoriaCosmetico, string>;

// O equipado de cada categoria, se ainda está disponível; senão, o padrão.
export function equipadosValidos(catalogo: readonly Cosmetico[], equipados: Partial<Equipados>, disp: ReadonlySet<string>): Equipados {
  const out = { ...PADRAO_DA_CATEGORIA };
  for (const cat of CATEGORIAS_DE_COSMETICO) {
    const id = equipados[cat];
    if (id && disp.has(id) && catalogo.find((x) => x.id === id)?.categoria === cat) out[cat] = id;
  }
  return out;
}

export interface CosmeticoNaTela extends Cosmetico {
  tem: boolean; // pode equipar
  equipado: boolean;
  comprado: boolean; // está na conta pela loja (fica mesmo se o plano descer)
  aVenda: boolean; // dá para comprar agora (está na loja e o jogador ainda não tem)
}
export interface Colecao { itens: CosmeticoNaTela[]; equipados: Equipados; plano: Plano; saldo: number | null }

export function colecaoNaTela(catalogo: readonly Cosmetico[], plano: Plano, naConta: ReadonlyMap<string, OrigemCosmetico>, equipados: Partial<Equipados>, saldo: number | null): Colecao {
  const disp = disponiveis(catalogo, plano, naConta.keys()), eq = equipadosValidos(catalogo, equipados, disp), loja = new Set(daLoja(catalogo).map((x) => x.id));
  const itens = [...catalogo].sort((a, b) => a.ordem - b.ordem).map((x) => ({
    ...x, tem: disp.has(x.id), equipado: eq[x.categoria] === x.id, comprado: naConta.get(x.id) === 'loja', aVenda: loja.has(x.id) && !disp.has(x.id),
  }));
  return { itens, equipados: eq, plano, saldo };
}

export type ErroCosmetico = 'inexistente' | 'nao_tem' | 'nao_vende' | 'ja_tem' | 'sem_saldo';
export const MENSAGEM_DO_COSMETICO: Record<ErroCosmetico, string> = {
  inexistente: 'Esse item não existe.',
  nao_tem: 'Esse item ainda não é seu.',
  nao_vende: 'Esse item não está na loja.',
  ja_tem: 'Esse item já é seu.',
  sem_saldo: 'Tentos insuficientes para esse item.',
};
export class ErroDoCosmetico extends Error {
  readonly codigo: ErroCosmetico;
  constructor(codigo: ErroCosmetico) {
    super(MENSAGEM_DO_COSMETICO[codigo]);
    this.codigo = codigo;
  }
}

// Na mesa: a moldura de cada pessoa e, na sala privada, a mesa e o verso do dono.
export interface VisualDaMesa { mesa: string; verso: string }
